import {
    getPageFromURL,
    getURLParam,
    hasURLParam,
    removeURLParam,
    setURLParam
} from 'hooks/use-url-params-utils';
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook for managing page-based URL parameters
 * @param {string} paramName - The name of the page parameter (default: 'page')
 * @param {number} defaultPage - Default page number (default: 1)
 * @returns {Array} [page, setPage, nextPage, prevPage]
 */
export const usePageParam = (paramName = 'page', defaultPage = 1) => {
    const [page, setPageState] = useState(() => {
        // Initialize state with URL value immediately
        return getPageFromURL(paramName, defaultPage);
    });

    // Initialize page from URL on mount (backup for SSR)
    useEffect(() => {
        const pageNumber = getPageFromURL(paramName, defaultPage);
        if (pageNumber !== page) {
            setPageState(pageNumber);
        }
    }, [paramName, defaultPage, page]);

    // Set page and update URL
    const setPage = useCallback(
        (newPage) => {
            setPageState(newPage);
            setURLParam(paramName, String(newPage));
        },
        [paramName]
    );

    return [page, setPage];
};

/**
 * Custom hook for managing search parameters
 * @param {string} paramName - The name of the search parameter (default: 'q')
 * @returns {Array} [searchTerm, setSearchTerm, clearSearch, hasSearch]
 */
export const useSearchParam = (paramName = 'q') => {
    const [searchTerm, setSearchTermState] = useState('');

    // Initialize search term from URL on mount
    useEffect(() => {
        const urlSearchTerm = getURLParam(paramName);
        if (urlSearchTerm !== null && urlSearchTerm !== undefined) {
            setSearchTermState(urlSearchTerm);
        }
    }, [paramName]);

    // Set search term and update URL
    const setSearchTerm = useCallback(
        (term) => {
            setSearchTermState(term);
            if (term && term.trim() !== '') {
                setURLParam(paramName, term.trim());
            } else {
                removeURLParam(paramName);
            }
        },
        [paramName]
    );

    // Check if search term exists
    const hasSearch = hasURLParam(paramName);

    return [searchTerm, setSearchTerm, hasSearch];
};
